import { useState } from 'react';
import { usePageParam } from '../../hooks/usePageParam';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Pagination from '../../components/common/Pagination';
import { RELEASE_NOTES_QUERY } from '../../graphql/queries';
import styles from './ReleaseNotes.module.css';
import Icon from '../../components/common/Icon';

const PAGE_SIZE = 30;

interface ReleaseNoteListItem {
  id: string;
  version: string;
  releaseDate: string;
  items: { title: string }[];
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

const ReleaseNotes = () => {
  const navigate = useNavigate();
  const [page, setPage] = usePageParam();
  const { data, loading, error } = useQuery(RELEASE_NOTES_QUERY, {
    variables: { page, limit: PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });
  const notes: ReleaseNoteListItem[] = data?.releaseNotes?.notes ?? [];
  const total: number = data?.releaseNotes?.total ?? 0;

  return (
    <DashboardLayout topBarTitle="Release Notes">
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Release Notes</h2>
          <p className="text-on-surface-variant mt-1">What's new in each version of the platform.</p>
        </div>

        {loading && notes.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <Icon name="refresh" className="animate-spin mr-2" />
            Loading release notes...
          </div>
        ) : error && notes.length === 0 ? (
          <div role="alert" className="p-4 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
            <Icon name="error" className="text-base" />
            Could not load the release notes: {error.message}
          </div>
        ) : notes.length === 0 ? (
          <div className="py-16 text-center bg-white rounded-xl">
            <Icon name="campaign" className="text-4xl text-outline mb-3 block" />
            <p className="text-on-surface-variant">No release notes published yet</p>
          </div>
        ) : (
          <>
          <div className="space-y-4">
            {notes.map((note) => (
              <div key={note.id} className={styles.card}>
                <div className={styles.cardHead}>
                  <button className={styles.versionLink} onClick={() => navigate(`/release-notes/${encodeURIComponent(note.version)}`)}>
                    Version {note.version}
                  </button>
                  <span className={styles.date}>{formatDate(note.releaseDate)}</span>
                  <button
                    className={styles.detailsBtn}
                    onClick={() => navigate(`/release-notes/${encodeURIComponent(note.version)}`)}
                  >
                    Details
                    <Icon name="arrow_forward" className="text-base" />
                  </button>
                </div>
                <ul className={styles.titleList}>
                  {note.items.map((item, i) => (
                    <li key={i} className={styles.titleRow}>
                      <Icon name="check_circle" className="text-base text-primary" />
                      {item.title}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} noun="release notes" variant="card" />
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReleaseNotes;
